<template>
  <div>
    <div class="header-block">
      <div class="d-flex justify-content-between">
        <div>Orientačný limit úveru podľa DTI</div>
        <div>
          <v-progress-circular
            v-if="dtiLoading"
            class="loading-spinner extrasmall"
            indeterminate
          ></v-progress-circular>
          <div class="bold" v-else>{{ estimatedDti?.value | dotTheAmount }} EUR</div>
        </div>
      </div>
      <div class="d-flex justify-content-between">
        <div>Akceptovaný príjem / mes.</div>
        <div>
          <v-progress-circular
            v-if="dtiLoading"
            class="loading-spinner extrasmall"
            indeterminate
          ></v-progress-circular>
          <div class="bold" v-else>{{ estimatedDti?.extra?.prijem_mo | dotTheAmount }} EUR</div>
        </div>
      </div>
      <div v-if="income?.error">
        <br>
        <div
          v-for="(error, key) in income.error"
          :key="key"
          class="error-text"
        >
          {{ error }}
        </div>
      </div>
      <div v-if="previewError" class="error-text" role="alert">{{ previewError }}</div>
      <div v-for="warning in rentalWarnings" :key="warning.code" class="error-text rental-bank-warning">
        <strong>{{ warning.name }}:</strong> {{ warning.message }}
      </div>
    </div>
    <form @submit.prevent="saveIncome" id="income-submit">
      <div class="income-methodology-note">
        Príjem z prenájmu sa uloží ako doplnkový príjem. Zadajte mesačné nájomné,
        odkedy ho klient poberá a či už bol príjem zdanený. Každá banka následne
        akceptuje jeho výšku podľa vlastnej metodiky.
      </div>
      <div class="input-group">
        <label for="najomne" class="field-label"
        >Výška mesačného nájomného<strong></strong></label
        ><input
        @change="showEstimatedDtiToUser"
        v-model.number="form.suma"
        type="number"
        step="0.01"
        class="text-input w-input required"
        maxlength="256"
        name="najomne"
        data-name="najomne"
        placeholder=""
        id="najomne"
        required=""
      />
      </div>
      <div class="input-group">
        <label for="prijem-od" class="field-label">Neprerušený príjem nájmu od</label>
        <v-menu
          v-model="monthPickerOpen"
          :close-on-content-click="false"
          attach
          offset-y
          max-width="290"
          min-width="290"
        >
          <template v-slot:activator="{ on, attrs }">
            <button
              ref="monthPickerButton"
              id="prijem-od"
              type="button"
              class="text-input w-input rental-month-button"
              :aria-invalid="monthError ? 'true' : 'false'"
              aria-describedby="prijem-od-help"
              v-bind="attrs"
              v-on="on"
            >
              <span>{{ formattedRentalMonth || 'Vyberte mesiac a rok' }}</span>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                <rect x="3" y="5" width="18" height="16" rx="2" />
                <path d="M16 3v4M8 3v4M3 11h18" />
              </svg>
            </button>
          </template>
          <v-date-picker
            :value="form.prijem_od"
            type="month"
            locale="sk-SK"
            :max="getTodayDate()"
            :min="'0001-01'"
            no-title
            @input="selectRentalMonth"
          />
        </v-menu>
        <p id="prijem-od-help" :class="{ 'error-text': monthError }" :role="monthError ? 'alert' : null" class="rental-month-help">
          {{ monthError || 'Vyberte mesiac a rok, odkedy klient nepretržite poberá nájomné.' }}
        </p>
      </div>
      <div class="dropdown-input-custom rest-input">
        <dropdown-select
          @select="setDropdownValue($event, 'prijem_zdaneny')"
          :id="'rental_prijem_zdaneny'"
          :init-value="form.prijem_zdaneny"
          :options="taxedChoices"
          normal
          :name="'Bol príjem už zdanený?'"
          required
        />
      </div>
    </form>
  </div>
</template>

<script>
import DropdownSelect from "../../../DropdownSelect";
import { getRentDTI } from "@/services/brokerService";
import vuetify from "@/plugins/vuetify";

export default {
  name: "RentalIncome",
  vuetify,
  components: {DropdownSelect},
  props: {
    income: [Object, null],
    formTemplate: Object,
  },
  created () {
    if (this.income) {
      this.form.suma = this.income.suma;
      this.form.prijem_od = this.translateDateFromDbValue(this.income.zamestnanie_od);
      this.form.prijem_zdaneny = this.income.zdanenie_minuly_rok;
      this.showEstimatedDtiToUser();
    }
  },
  computed: {
    rentalWarnings () {
      const names = {slsp: 'SLSP', csob: 'ČSOB', vub: 'VÚB', mbank: 'mBank', unicredit: 'UniCredit', prima: 'Prima banka', '365': '365.bank', tatra: 'Tatra banka'};
      return Object.entries(this.rentalResults).filter(([, result]) => result.success === false).map(([code, result]) => {
        let message = (result.details || []).join(' ');
        const minimum = result.extra?.min_trvanie_prenajmu;
        if (minimum != null && minimum > 0 && !/mesiac/.test(message)) message += ` Minimálne trvanie nájmu: ${minimum} mesiacov.`;
        return {code, name: names[code] || code, message};
      });
    },
    formattedRentalMonth () {
      if (!this.isValidRentalMonth(this.form.prijem_od)) return '';
      const [year, month] = this.form.prijem_od.split('-');
      return `${month}/${year}`;
    },
  },
  methods: {
    saveIncome () {
      if (!this.isValidRentalMonth(this.form.prijem_od)) {
        this.monthError = 'Vyberte mesiac a rok začiatku nájmu, najneskôr aktuálny mesiac.';
        this.$refs.monthPickerButton.focus();
        return;
      }
      this.monthError = '';
      this.$emit('income-save-error', '');
      const incomeObject = {
        typ: "vedlajsi",
        druh: "prijem_z_prenajmu_nehnutelnosti",
        suma: this.form.suma,
        zamestnanie_od: this.translateDateToDbValue(this.form.prijem_od),
        zdanenie_minuly_rok: this.form.prijem_zdaneny,
      };
      if (this.income) {
        incomeObject.id = this.income.id;
      }
      this.$emit("income-saved", {incomeInfo: incomeObject});
    },
    isValidRentalMonth (value) {
      return typeof value === 'string'
        && /^(?!0000)\d{4}-(0[1-9]|1[0-2])$/.test(value)
        && value <= this.getTodayDate();
    },
    selectRentalMonth (value) {
      this.form.prijem_od = value;
      this.monthError = '';
      this.$emit('income-save-error', '');
      this.monthPickerOpen = false;
      this.showEstimatedDtiToUser();
      this.$nextTick(() => this.$refs.monthPickerButton.focus());
    },
    async showEstimatedDtiToUser () {
      const requestId = ++this.previewRequestId;
      this.estimatedDti = null;
      this.rentalResults = {};
      this.previewError = '';
      if (!this.checkValue(this.form.suma) || !this.isValidRentalMonth(this.form.prijem_od)) {
        this.dtiLoading = false;
        return;
      }
      this.dtiLoading = true;
      try {
        const dtiData = await getRentDTI({
          suma: this.form.suma,
          od: this.translateDateToDbValue(this.form.prijem_od),
          zdanenie_minuly_rok: this.form.prijem_zdaneny === true,
        });
        if (requestId !== this.previewRequestId) return;
        this.rentalResults = dtiData;
        this.pickHighestDti(dtiData);
      } catch (error) {
        if (requestId === this.previewRequestId) this.previewError = 'Náhľad príjmu sa nepodarilo načítať. Skúste prepočet zopakovať.';
      } finally {
        if (requestId === this.previewRequestId) this.dtiLoading = false;
      }
    },
    checkValue(value) {
      return !(value === undefined || value === null || value === "");
    },
    pickHighestDti (dtiData) {
      let valueDti = 0;
      for (const value of Object.entries(dtiData)) {
        for (const valueElement of value) {
          if (valueDti < valueElement.value) {
            valueDti = Math.round(valueElement.value);
            this.estimatedDti = valueElement;
          }
        }
      }
    },
    translateDateFromDbValue (dateToTransform) {
      if (typeof dateToTransform !== 'string') return null;
      const month = dateToTransform.slice(0, 7);
      return this.isValidRentalMonth(month) ? month : null;
    },
    translateDateToDbValue (dateToTransform) {
      if (!this.isValidRentalMonth(dateToTransform)) return null;
      return dateToTransform + "-01";
    },
    setDropdownValue (value, dropdownName) {
      this.form[dropdownName] = value;
      this.showEstimatedDtiToUser();
    },
    getTodayDate () {
      const today = new Date();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const yyyy = today.getFullYear();
      return yyyy + "-" + mm;
    },
  },
  data () {
    return {
      dtiLoading: false,
      rentalResults: {},
      previewError: '',
      previewRequestId: 0,
      monthPickerOpen: false,
      monthError: '',
      estimatedDti: null,
      form: {
        suma: null,
        typ: "vedlajsi",
        druh: "prijem_z_prenajmu_nehnutelnosti",
        prijem_od: null,
        prijem_zdaneny: null,
      },
      taxedChoices: [
        {
          display_name: "áno",
          value: true,
        },
        {
          display_name: "nie",
          value: false,
        },
      ],
    };
  },
};
</script>

<style scoped lang="scss">
.income-methodology-note {
  margin-top: 20px;
  padding: 16px 18px;
  border: 1px solid rgba(93, 174, 241, 0.35);
  border-radius: 16px;
  color: var(--theme-text-secondary, #66738f);
  line-height: 1.45;
}

.rental-month-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  text-align: left;
  color: var(--app-text, inherit);
  background: var(--app-surface, #fff);
  cursor: pointer;
}

.rental-month-button:focus-visible {
  outline: 2px solid var(--app-primary, #5daef1);
  outline-offset: 2px;
}

.rental-month-help {
  width: 100%;
  margin: 4px 0 0;
  font-size: 12px;
}
</style>
